import React, { FC, useContext, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { WarningMessage } from '@eika/attention-message';
import { CheckboxPanels } from '@eika/checkbox';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { ExpandableInput } from '@eika/expandable-input';
import { IconTypes } from '@eika/icon';

import { AppContext } from '../../containers/appContextContainer';
import { Laaneformaal, LaantypeWeb, SoeknadstypeWeb, ValgtWeb } from '../../generated-types';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { useSoeknadLaan, useUpdateSoeknadLaan } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import FlytteGjeld from './flytteGjeld';
import FlytteLaan from './flytteLaan';
import { maksLaanebeloep, showWarning60Precent, showWarning90Precent } from './laaneberegning';
import LaaneberegningKredittInfo from './laaneberegningKredittInfo';
import LaaneberegningUtlaanInfo from './laaneberegningUtlaanInfo';
import Makslaanebeloep from './makslaanebeloep';
import Nedbetalingstid, { NedbetalingstidFormData } from './nedbetalingstid';

export type LaanebeloepFormData = NedbetalingstidFormData & {
    oekeLaan: ValgtWeb;
    soektBeloep: number | undefined;
    eksisterendeLaanebeloep: number | undefined;
    laaneformaal: Laaneformaal[] | undefined;
    annetLaaneformaal: string | undefined;
    laangiver: string | undefined;
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Laanebeloep: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { data: featureToggles } = useFeatureToggle();
    const { utvideDatagrunnlag } = featureToggles || {};
    const { soeknadstype } = useContext(AppContext);
    const { laanebeloep, validationMessage } = useTranslation();
    const { data: soeknadLaan } = useSoeknadLaan(soeknadstype);
    const { mutate: updateSoeknadLaan, isPending } = useUpdateSoeknadLaan(soeknadLaan, () => {
        navigate(navigateTo);
    });
    const laan = soeknadLaan?.laan;
    const methods = useForm<LaanebeloepFormData>({
        defaultValues: {
            soektBeloep: undefined,
            eksisterendeLaanebeloep: undefined,
            nedbetalingstid: 30,
            oekeLaan: ValgtWeb.IKKE_VALGT,
            laaneformaal: [],
            annetLaaneformaal: undefined,
        },
    });
    const {
        control,
        reset,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
    } = methods;

    useEffect((): void => {
        if (laan) {
            reset({
                eksisterendeLaanebeloep: laan.eksisterendeLaanebeloep || undefined,
                soektBeloep: laan.soektBeloep || undefined,
                nedbetalingstid: laan.nedbetalingstid ? laan.nedbetalingstid / 12 : 30,
                oekeLaan: soeknadLaan?.oekeLaan,
                laaneformaal: soeknadLaan?.laaneformaal || [],
                annetLaaneformaal: soeknadLaan?.annetLaaneformaal || '',
                laangiver: laan.laangiver || undefined,
            });
        }
    }, [soeknadLaan, laan, reset]);

    const onSubmitForm = (data: LaanebeloepFormData) => {
        if (soeknadLaan && isDirty) {
            updateSoeknadLaan({
                soeknadId: soeknadLaan.soeknadId,
                soektBeloep: data.soektBeloep || null,
                nedbetalingstid: data.nedbetalingstid * 12,
                eksisterendeLaanebeloep: data.eksisterendeLaanebeloep || null,
                oekeLaan: data.oekeLaan,
                laaneformaal: data.laaneformaal || [],
                annetLaaneformaal: data.annetLaaneformaal || null,
                laangiver: data.laangiver || null,
            });
        } else {
            navigate(navigateTo);
        }
    };

    const { soektBeloep, laaneformaal } = watch();

    const options: { label: string; value: Laaneformaal }[] = [
        { label: laanebeloep.formaalOppussing, value: Laaneformaal.OPPUSSING },
        { label: laanebeloep.formaalByggeBolig, value: Laaneformaal.BYGGE_PAA_BOLIG },
        { label: laanebeloep.formaalKjoepeBil, value: Laaneformaal.KJOEPE_BIL },
        { label: laanebeloep.formaalInnfriGjeld, value: Laaneformaal.INNFRI_GJELD },
        { label: laanebeloep.formaalAnnet, value: Laaneformaal.ANNET },
    ];

    return (
        <div>
            <PageCard title={laanebeloep.title}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmitForm)} className="row row-gap-large">
                        {soeknadstype === SoeknadstypeWeb.REFINANSIERE_BOLIGLAAN && (
                            <>
                                {soeknadLaan?.eiendomVerdi && <Makslaanebeloep soeknadLaan={soeknadLaan} />}
                                <Controller
                                    name="soektBeloep"
                                    control={control}
                                    rules={{
                                        required: `${validationMessage.requiredValue}`,
                                    }}
                                    render={({ field: { onChange } }) => (
                                        <CurrencyInput
                                            name="soektBeloep"
                                            className="max-width-200"
                                            label={laanebeloep.labelLaaneoekning}
                                            sublabel={
                                                soeknadLaan?.eiendomVerdi
                                                    ? laanebeloep.subLabelOensketLaanebeloep.replace(
                                                          '{maksoekning}',
                                                          formatCurrencyValue(maksLaanebeloep(soeknadLaan)),
                                                      )
                                                    : ''
                                            }
                                            type="tel"
                                            error={errors.soektBeloep?.message}
                                            value={soektBeloep}
                                            onValueChange={(v) => onChange(v.value)}
                                        />
                                    )}
                                />
                                {soeknadLaan?.eiendomVerdi && showWarning60Precent(soeknadLaan, soektBeloep) && (
                                    <WarningMessage
                                        id="warning60Precent"
                                        text={laanebeloep.warningLaaneoekning60}
                                        icon={IconTypes.INFO_24}
                                    />
                                )}
                                {soeknadLaan?.eiendomVerdi && showWarning90Precent(soeknadLaan, soektBeloep) && (
                                    <WarningMessage
                                        id="warning85Precent"
                                        text={laanebeloep.warningLaaneoekning90}
                                        icon={IconTypes.INFO_24}
                                    />
                                )}
                                {utvideDatagrunnlag && (
                                    <Controller
                                        name="laaneformaal"
                                        control={control}
                                        rules={{
                                            required: `${validationMessage.requiredValue}`,
                                        }}
                                        render={({ field }) => {
                                            const fieldValue = field.value || [];
                                            return (
                                                <CheckboxPanels
                                                    label={laanebeloep.formaal}
                                                    error={errors.laaneformaal?.message}
                                                    options={options.map((option) => ({
                                                        label: option.label,
                                                        value: option.value.toString(),
                                                        checked: fieldValue.includes(option.value),
                                                        onValueChange: (checked) => {
                                                            const newValue = checked
                                                                ? [...fieldValue, option.value]
                                                                : fieldValue.filter((val) => val !== option.value);
                                                            field.onChange(newValue);
                                                        },
                                                    }))}
                                                />
                                            );
                                        }}
                                    />
                                )}
                                {laaneformaal?.includes(Laaneformaal.ANNET) && utvideDatagrunnlag && (
                                    <Controller
                                        name="annetLaaneformaal"
                                        control={control}
                                        rules={{
                                            required: `${validationMessage.requiredValue}`,
                                        }}
                                        render={({ field }) => (
                                            <ExpandableInput
                                                label={laanebeloep.formaalAnnetTitle}
                                                error={errors.annetLaaneformaal?.message}
                                                maxLength={200}
                                                value={field.value}
                                                onChange={field.onChange}
                                                showCharCounter
                                            />
                                        )}
                                    />
                                )}
                                {(soeknadLaan?.laan.laantype === LaantypeWeb.UTLAAN ||
                                    soeknadLaan?.laan.laantype === null) && (
                                    <>
                                        <Nedbetalingstid />
                                        <LaaneberegningUtlaanInfo soeknadLaan={soeknadLaan} />
                                    </>
                                )}
                                {(soeknadLaan?.laan.laantype === LaantypeWeb.KREDITT ||
                                    soeknadLaan?.laan.laantype === LaantypeWeb.KREDITT_UTLAAN) && (
                                    <LaaneberegningKredittInfo />
                                )}
                            </>
                        )}
                        {soeknadstype === SoeknadstypeWeb.FLYTTE_BOLIGLAAN && (
                            <>
                                <FlytteGjeld />
                                {soeknadLaan?.eiendomVerdi &&
                                    soeknadLaan.laan.eksisterendeLaanebeloep &&
                                    soeknadLaan.laan.eksisterendeLaanebeloep > 0 && (
                                        <Makslaanebeloep soeknadLaan={soeknadLaan} />
                                    )}
                                <FlytteLaan soeknadLaan={soeknadLaan} featureToggle={utvideDatagrunnlag} />
                            </>
                        )}
                        <PageFooter
                            soeknadId={soeknadLaan?.soeknadId}
                            onForrigeClick={() => navigate(navigateFrom)}
                            showSpinner={isPending}
                        />
                    </form>
                </FormProvider>
            </PageCard>
        </div>
    );
};

export default Laanebeloep;
